import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';

type Variant = 'default' | 'primary' | 'danger' | 'success';

const variants: Record<Variant, string> = {
  default:
    'text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-primary-500',
  primary:
    'text-primary-600 hover:bg-primary-50 hover:text-primary-700 focus-visible:ring-primary-500',
  danger:
    'text-danger-600 hover:bg-danger-50 hover:text-danger-700 focus-visible:ring-danger-500',
  success:
    'text-success-600 hover:bg-success-50 hover:text-success-600 focus-visible:ring-success-500',
};

/**
 * Compact icon-only action button. `label` is used both as the accessible name
 * (aria-label, for screen readers) and the native hover tooltip (title), so the
 * action is discoverable by mouse and assistive tech alike (WCAG 2.2 AA).
 */
export default function IconButton({
  icon: Icon,
  label,
  variant = 'default',
  className,
  ...rest
}: {
  icon: LucideIcon;
  label: string;
  variant?: Variant;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'title'>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx(
        'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-40',
        variants[variant],
        className,
      )}
      {...rest}
    >
      <Icon className="h-4 w-4" aria-hidden={true} />
    </button>
  );
}

export { IconButton };
